import { t } from '@lingui/macro';
import { useCallback } from 'react';

import { fetchJson } from '@api/ApiHelper';
import { ToastLooks, useCreateToast } from '@components/common/toast';

interface MergeIdentitiesParams {
  sourceIdentityId: string;
  destinationIdentityId: string;
}

interface MergeIdentitiesResponse {
  id: string;
  identity_name: string;
  source_url: string;
}

export const useMergeIdentities = (
  onSuccess?: () => void
): { mergeIdentities: (params: MergeIdentitiesParams) => Promise<boolean> } => {
  const { toast } = useCreateToast();

  const mergeIdentities = useCallback(
    async ({ sourceIdentityId, destinationIdentityId }: MergeIdentitiesParams): Promise<boolean> => {
      try {
        // Get brandfolder key from global BFG object
        const brandfolderKey = BFG.brandfolder_key;

        if (!brandfolderKey) {
          toast(t`Brandfolder not found`, ToastLooks.Error);
          return false;
        }

        if (!sourceIdentityId || !destinationIdentityId) {
          toast(t`Source and destination identity IDs are required`, ToastLooks.Error);
          return false;
        }

        // Make API call to merge identities
        await fetchJson<MergeIdentitiesResponse>({
          url: `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/identities/merge`,
          method: 'PATCH',
          body: {
            destination_identity_id: destinationIdentityId,
            source_identity_id: sourceIdentityId,
          },
        });

        // Call success callback if provided (e.g., to refetch data)
        onSuccess?.();

        return true;
      } catch (error) {
        toast(t`Failed to merge identities. Please try again.`, ToastLooks.Error);
        return false;
      }
    },
    [onSuccess, toast]
  );

  return { mergeIdentities };
};
